<template>
  <div class="tournament" :data-qe-id="`tournament-${tournament.joined ? 'overview' : 'registration'}`">
    <template v-if="!tournament.joined">
      <div class="join-teaser">
        <c-lobby-status
          class="lobby-status"
          v-if="tournamentUi.tournamentStatus !== TournamentStatus.FINISHED"
          :timers="tournamentUi.lobbyTimers"
          @lobby-timer-complete="onLobbyTimerComplete"
          is-banner
        />
        <t-container>
          <c-masters-registration-panel
            v-if="tournamentUi.isMasters"
            :tournamentUi="tournamentUi"
            @register="register(false)"
          />
          <c-tournament-registration-panel v-else :tournamentUi="tournamentUi" @register="register(false)" />
        </t-container>
      </div>
    </template>

    <template v-else>
      <t-text class="tournament-header">
        <img v-if="tournamentImage" :src="tournamentImage" class="tournament-image" data-qe-id="tournament-image" />
        <h1 class="tournament-headline" data-qe-id="tournament-name">{{ tournamentName }}</h1>
        <span
          class="winning-distribution-info"
          @click="events.$emit('tournament-info-modal', { tournamentUi, trackId: 'header' })"
          data-qe-id="win-distribution-trigger"
        >
          <i class="icf-info icon-info"></i>
        </span>
      </t-text>

      <c-joined-template :isMasters="tournamentUi.isMasters" class="joined-template">
        <c-lobby-status
          class="lobby-status"
          v-if="tournamentUi.tournamentStatus !== TournamentStatus.FINISHED"
          :timers="tournamentUi.lobbyTimers"
          @lobby-timer-complete="onLobbyTimerComplete"
          is-banner
        />
        <c-games-overview @onGame="onGame" :games="games">
          <template v-if="tournament.tournamentRank" v-slot:header>
            <c-tournament-laurel
              class="tournament-laurel"
              :state="allGamesPlayed ? TOURNAMENT_LAUREL_STATE.FINISHED : TOURNAMENT_LAUREL_STATE.RUNNING"
              :score="tournament.games.length > 0 ? tournament.tournamentScore : '-'"
              :rank="tournament.tournamentRank"
              :prev-best-score="store.state.ranking.tournament.prevBestScore"
            ></c-tournament-laurel>
          </template>
        </c-games-overview>
        <p
          v-if="tournament.metaData.clearancePhase"
          data-qe-id="tournament-clearance-phase"
          class="t-inlay-tip clearance-phase-inlay-tip t-tc"
        >
          {{ $t('tViews.joined.clearance_info') }}
        </p>
        <div v-else class="c-game-buttons-section">
          <c-tech-hint
            v-if="!allGamesPlayed"
            :techs="tournament.metaData.gameTechs"
            :gametype="tournamentUi.gametype"
            class="c-game-button"
          >
            <template v-slot:default="status">
              <t-tooltip
                :disabled="!(!hasNextGame || !isLobbyOpen) || status.block"
                :text="$t('tViews.joined.button_tooltip_' + (!isLobbyOpen ? 'lobbyClosed' : 'pending'))"
              >
                <!-- todo check disabled state with t-button -->
                <t-button
                  :inactive="status.block"
                  :disabled="!(hasNextGame && isLobbyOpen) && !status.block"
                  @click="playGame(status.block)"
                  yellow
                  l
                  data-qe-id="play-button"
                >
                  {{ $t('tViews.joined.button_play') }}
                </t-button>
              </t-tooltip>
            </template>
          </c-tech-hint>
          <c-tech-hint
            :techs="tournament.metaData.gameTechs"
            :gametype="tournamentUi.gametype"
            class="c-game-button"
            :skipCheck="rejoinNotPossible"
          >
            <template v-slot:default="status">
              <t-tooltip :disabled="!rejoinNotPossible || status.block" :text="rejoinTooltipText" class="c-game-button">
                <t-button
                  @click="register(true, status.block)"
                  :inactive="status.block"
                  :disabled="rejoinNotPossible && !status.block"
                  gray
                  l
                  data-qe-id="rejoin-button"
                >
                  {{ $t('tViews.joined.button_rejoin') }}
                </t-button>
              </t-tooltip>
            </template>
          </c-tech-hint>
        </div>
      </c-joined-template>
    </template>

    <!-- this element is only to enapsulate the logic for the registration
          overlay - it will render no HTML in here -->
    <m-tournament-registration @registration-complete="onRegistrationComplete" ref="tournamentRegistration" />
    <m-result-modal
      v-if="showResultModal"
      @close="showResultModal = false"
      :competition-id="gameSelectedCompetitionId"
    />

    <!-- <c-game-stats
      v-if="showGameStats"
      @onClose="showGameStats = false"
      :competitionResult="$store.state.history.selectedCompetitionResult"
      :userId="$store.state.user.id"
      :processing="loadingGamesStats"
    ></c-game-stats> -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import events from '@gd/event-bus'
import { useStore } from '@/store'

import { User } from '@gd/storage'
import CGamesOverview from '@/components/CGamesOverview.vue'
import CLobbyStatus from '@/components/CLobbyStatus.vue'
import CJoinedTemplate from '@/components/CJoinedTemplate.vue'
import CMastersRegistrationPanel from '@/components/tournamentsView/CMastersRegistrationPanel.vue'
import CTournamentRegistrationPanel from '@/components/tournamentsView/CTournamentRegistrationPanel.vue'
import CTournamentLaurel from '@/components/CTournamentLaurel.vue'
import { TOURNAMENT_LAUREL_STATE as _TOURNAMENT_LAUREL_STATE } from '@/components/CTournamentLaurel.vue'
import MTournamentRegistration from '@/modules/MTournamentRegistration.vue'
import { Tournament, TournamentUi, GameUi } from '@/model/Tournament'
import MResultModal from '@/modules/MResultModal.vue'
import { Getters } from '@/store/getters'
import { TournamentStatus as _TournamentStatus } from '@/model/TournamentStatus'

const emit = defineEmits(['close'])
const router = useRouter()
const store = useStore()
const { t } = useI18n()
const tournamentRegistration = ref(null)
const TournamentStatus = _TournamentStatus
const gameSelectedCompetitionId = ref('')
const showResultModal = ref(false)
const TOURNAMENT_LAUREL_STATE: any = _TOURNAMENT_LAUREL_STATE

const tournamentName = computed(() => {
  return (store.getters.tournamentUi as TournamentUi).name
})

const tournamentImage = computed(() => {
  // return (store.getters.tournamentUi as TournamentUi).metaData.image || null
  return null
})

const games = computed(() => {
  return (store.getters as Getters).tournamentUi.games
})
/**
 * all games are played
 */
const allGamesPlayed = computed(() => {
  return (store.getters as Getters).tournamentUi.allGamesPlayed
})

const hasNextGame = computed(() => {
  return (store.getters as Getters).tournamentUi.hasNextGame
})

/**
 * The "tournamentUi.isLobbyOpen" doesn't consider if the competition is running
 */
const isLobbyOpen = computed(() => {
  const tournamentUi = (store.getters as Getters).tournamentUi
  return (
    tournamentUi.tournamentStatus === TournamentStatus.RUNNING &&
    tournamentUi.lobbyTimers.start.totalSecs === 0 &&
    tournamentUi.lobbyTimers.end.totalSecs > 0
  )
})

const tournament = computed((): Tournament => {
  return store.state.tournament
})

const tournamentUi = computed((): TournamentUi => {
  return store.getters.tournamentUi
})

function register(rejoin: boolean, block?: boolean) {
  if (block) {
    return
  }

  if ((store.state.user as User).forcePayout) {
    router.replace('/extern/payout')
    return
  }
  tournamentRegistration.value.show({ rejoin })
}

function onRegistrationComplete() {
  events.$emit('update-balance')
  store.dispatch('ranking/tournament/fetch', tournament.value.tournamentId)
  if (tournamentUi.value.isMasters) {
    store.dispatch('ranking/season/fetch', store.state.zone.zoneId)
  }
}

function onGame(game: GameUi) {
  gameSelectedCompetitionId.value = game.competitionId
  showResultModal.value = true
  //  // for the first tournament version we don't have a click functionality on the games
  //  // - this will be added later - so keep the code which opens the gameStats
  // this.loadingGamesStats = true
  // showGameStats.value = true
  // store
  //   .dispatch('history/fetchGameHistoryById', game.competitionId)
  //   .then(() => {
  //     this.loadingGamesStats = false
  //     console.log(
  //       `Fetched game history for game ${
  //         game.competitionId
  //       }. Now switch into game history view to display retrieved data.`
  //     )
  //   })
  //   .catch((err: Error) => {
  //     this.loadingGamesStats = false
  //     console.warn(err)
  //   })
}

function playGame(blocked: boolean) {
  if (blocked || !hasNextGame.value || !isLobbyOpen.value) {
    return
  }
  router.push(`/matching/${store.state.zone.zoneId}/${(store.state.tournament as Tournament).tournamentId}`)
}

function onLobbyTimerComplete() {
  store.dispatch('fetchTournament', store.state.zone.zoneId).catch((err: any) => {
    if (err === 401) {
      router.replace('/extern/login')
    }
  })
}

const userName = computed((): string => {
  return store.state.user.name
})
const rejoinLimitReached = computed((): boolean => {
  const tournament: Tournament = store.state.tournament
  return !tournament.metaData.rejoinsUnlimited && tournament.metaData.rejoinsRemaining === 0
})
const rejoinTooltipText = computed(() => {
  if (registrationIsOver.value) {
    return t('tViews.joined.button_rejoin_tooltip_registrationTimeOver')
  }
  if (rejoinLimitReached.value) {
    return t('tViews.joined.button_rejoin_tooltip_rejoinLimitReached')
  }
  return t('tViews.joined.button_rejoin_tooltip_noGamesPlayed')
})
const rejoinNotPossible = computed((): boolean => {
  const tournament: Tournament = store.state.tournament
  return tournament.games.length === 0 || rejoinLimitReached.value || registrationIsOver.value
})
/**
 * the case that the registration time ended and a tournament is not
 * in running mode is not handled ( because it should not happen )
 */
const registrationIsOver = computed(() => {
  return !(
    tournamentUi.value.isRegistrationPossible || tournamentUi.value.tournamentStatus === TournamentStatus.UPCOMING
  )
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import '~@npm/@gd/common-styles/lib/color-vars';
@import '../styles/vars';
.tournament {
  text-align: center;
  .clearance-phase-inlay-tip {
    display: inline-block;
    max-width: 80%;
  }

  .tournament-header {
    display: flex;
    justify-content: center;
    align-items: center;
    justify-items: center;
  }
  .tournament-image {
    margin-right: 1em;
    margin-block-start: 2em;
  }
  .tournament-headline {
    display: inline-block;
    text-transform: uppercase;
  }
  .tournament-laurel {
    margin-top: 1em;
  }
  .winning-distribution-info {
    cursor: pointer;
    .icon-info {
      color: @lobbyStatus--open;
      position: relative;
      font-size: 1.25em;
      top: 0.65em;
      margin-left: 0.55em;
      transition: color 150ms;
      &:hover {
        color: lighten(@lobbyStatus--open, 10%);
      }
    }
  }
  .lobby-status-banner {
    margin-bottom: 1em;
  }

  .modal-elements-hidden {
    // visibility: hidden;
  }
  .modal-content {
    position: relative;
    min-width: 20em;
    min-height: 5em;
    width: 100%;
  }
  .modal-processing .gd-modal-component-close {
    display: none;
  }
  .join-teaser {
    margin-top: 5em;
    position: relative;
  }
  .c-game-buttons-section {
    position: absolute;
    // fixes tooltip issue where button tooltip is behing the svg game slot number
    z-index: 2;
    bottom: -1.7em;
    width: 100%;
  }
  .c-game-button {
    position: relative;
    margin: 0 0.4em;
  }
  .joined-template {
    margin-top: 2em;
    margin-bottom: 7em;
    position: relative;
  }
  .lobby-status {
    position: absolute;
    left: 50.01%;
    transform: translateX(-50.01%);
    top: -1.5em;
    width: max-content;
  }
}
</style>
